// ✅ src/pages/VehicleV2/OwnVehicleMasterReport.jsx
// UPDATED FULL FILE
//
// Expected endpoints:
//   GET /api/ownVehicleMasterReport/init?branchId=&branchType=
//   GET /api/ownVehicleMasterReport/list?branch=&vehicleType=
//
// ✅ Updated pattern:
// - common localStorage / sessionStorage header pattern
// - useMemo + useCallback pattern
// - pageLoading + dropdownLoading + listLoading
// - prevents repeated initial API calls
// - safer search / view all / pagination / print flow
// - keeps branch locked for non-HO users
// - avoids infinite API re-calls caused by unstable init/list functions

import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaEye,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaSyncAlt,
  FaInfoCircle,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/ownVehicleMasterReport";

/* ---------------- helpers ---------------- */
const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const toNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const isSelect = (v) => {
  const s = toStr(v);
  return !s || s.toLowerCase() === "select";
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const normalizeOptionList = (arr, fallbackLabel = "Select") => {
  const list = toList(arr).map((x) => ({
    label: toStr(x.label ?? x.name ?? x.text ?? fallbackLabel, fallbackLabel),
    value: String(x.value ?? x.id ?? x.key ?? fallbackLabel),
  }));

  const hasSelect = list.some((x) => toStr(x.value).toLowerCase() === "select");

  if (!list.length) {
    return [{ label: fallbackLabel, value: fallbackLabel }];
  }

  return hasSelect
    ? list
    : [{ label: fallbackLabel, value: fallbackLabel }, ...list];
};

const defaultFilters = {
  branch: "Select",
  vehicleType: "Select",
};

const mapRow = (r, idx) => {
  const get = (...keys) => {
    for (const k of keys) {
      if (r && r[k] !== undefined && r[k] !== null && String(r[k]).trim() !== "") {
        return r[k];
      }
    }
    return "";
  };

  return {
    raw: r,
    id: get("id", "fld_id", "vehicleTypeId", "VehicleTypeId") || idx + 1,
    type: get(
      "type",
      "VehicleType",
      "fld_vehicletype",
      "fld_vehicletypename",
      "fld_vehicle_subtypename",
      "vehicletype"
    ),
    qty: toNum(get("qty", "Qty", "fld_qty", "quantity"), 0),
    value: toNum(
      get("value", "TotalPurchaseValue", "fld_totalpurchasevalue", "totalvalue"),
      0
    ),
  };
};

export default function OwnVehicleMasterReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);

  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ===========================
     shared local/session pattern
  =========================== */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toInt(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, branchType, userId]
  );

  const session = useMemo(
    () => ({
      branchId: sessionBranchId,
      branchType: toStr(branchType || "HO"),
      loginUserId: toInt(loginUserId, 0),
    }),
    [sessionBranchId, branchType, loginUserId]
  );

  /* ===========================
     refs
  =========================== */
  const didInitRef = useRef(false);

  /* ===========================
     loading states
  =========================== */
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  const isBusy = pageLoading || dropdownLoading || listLoading;

  /* ===========================
     ui states
  =========================== */
  const [showFilters, setShowFilters] = useState(true);

  /* ===========================
     dropdown states
  =========================== */
  const [branches, setBranches] = useState([{ label: "Select", value: "Select" }]);
  const [vehicleTypes, setVehicleTypes] = useState([
    { label: "Select", value: "Select" },
  ]);

  /* ===========================
     filter + grid states
  =========================== */
  const [filters, setFilters] = useState(defaultFilters);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  /* ===========================
     computed
  =========================== */
  const total = rows.length;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  );

  const currentItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  const canEditBranch = useMemo(
    () => toStr(session.branchType).toUpperCase() === "HO",
    [session.branchType]
  );

  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const pillClass = useMemo(
    () =>
      `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        isDark ? "bg-blue-900/60 text-blue-100" : "bg-blue-100 text-blue-800"
      }`,
    [isDark]
  );

  const formatCurrency = useCallback(
    (value) =>
      new Intl.NumberFormat("en-IN", {
        maximumFractionDigits: 0,
      }).format(toNum(value, 0)),
    []
  );

  /* ===========================
     dropdown load
  =========================== */
  const loadDropdowns = useCallback(async () => {
    try {
      setDropdownLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          branchId: session.branchId,
          branchType: session.branchType,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Failed to load dropdowns");
        return {
          branches: [{ label: "Select", value: "Select" }],
          vehicleTypes: [{ label: "Select", value: "Select" }],
        };
      }

      const nextBranches = normalizeOptionList(data.branches, "Select");
      const nextVehicleTypes = normalizeOptionList(data.vehicleTypes, "Select");

      setBranches(nextBranches);
      setVehicleTypes(nextVehicleTypes);

      return {
        branches: nextBranches,
        vehicleTypes: nextVehicleTypes,
      };
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Failed to load dropdowns"
      );

      const fallback = {
        branches: [{ label: "Select", value: "Select" }],
        vehicleTypes: [{ label: "Select", value: "Select" }],
      };

      setBranches(fallback.branches);
      setVehicleTypes(fallback.vehicleTypes);

      return fallback;
    } finally {
      setDropdownLoading(false);
    }
  }, [session, commonHeaders]);

  /* ===========================
     list load
  =========================== */
  const loadList = useCallback(
    async (nextFilters = defaultFilters) => {
      try {
        setListLoading(true);

        const finalFilters =
          toStr(session.branchType).toUpperCase() !== "HO"
            ? { ...nextFilters, branch: String(session.branchId) }
            : nextFilters;

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: {
            branch: finalFilters.branch,
            vehicleType: finalFilters.vehicleType,
          },
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load list");
        }

        const mapped = toList(data.rows || data.recordset).map(mapRow);
        setRows(mapped);
        setPage(1);

        return mapped;
      } catch (err) {
        console.error(err);
        setRows([]);
        toast.error(err?.response?.data?.message || err.message || "Failed to load list");
        return [];
      } finally {
        setListLoading(false);
      }
    },
    [session, commonHeaders]
  );

  /* ===========================
     initial load
  =========================== */
  const loadPage = useCallback(async () => {
    try {
      setPageLoading(true);

      const dropdownData = await loadDropdowns();

      let initialFilters = { ...defaultFilters };

      if (toStr(session.branchType).toUpperCase() !== "HO") {
        const matchedBranch =
          toList(dropdownData?.branches).find(
            (x) => String(x.value) === String(session.branchId)
          ) ||
          toList(dropdownData?.branches).find((x) => !isSelect(x.value));

        initialFilters.branch = matchedBranch
          ? String(matchedBranch.value)
          : String(session.branchId);
      }

      setFilters(initialFilters);
      await loadList(initialFilters);
    } finally {
      setPageLoading(false);
    }
  }, [loadDropdowns, loadList, session]);

  /* ===========================
     handlers
  =========================== */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSearch = useCallback(async () => {
    await loadList(filters);
    toast.info("Searching...");
  }, [filters, loadList]);

  const handleViewAll = useCallback(async () => {
    const reset =
      toStr(session.branchType).toUpperCase() !== "HO"
        ? {
            branch: String(session.branchId),
            vehicleType: "Select",
          }
        : defaultFilters;

    setFilters(reset);
    await loadList(reset);
    toast.info("Showing all records");
  }, [loadList, session]);

  const handleRefresh = useCallback(async () => {
    await loadPage();
  }, [loadPage]);

  const handlePrintAll = useCallback(() => {
    const params = new URLSearchParams({
      branch:
        toStr(session.branchType).toUpperCase() !== "HO"
          ? String(session.branchId)
          : filters.branch,
      vehicleType: filters.vehicleType,
    });

    navigate(`/vehicle/OwnVehicleMasterReport_Print?${params.toString()}`);
  }, [navigate, session, filters]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleOpenDetail = useCallback(
    (row) => {
      navigate("/vehicle/OwnVehicleReport", {
        state: {
          vehicleType: row?.type || "",
        },
      });
    },
    [navigate]
  );

  const goPage = useCallback(
    (p) => {
      const next = Math.min(Math.max(1, p), totalPages);
      setPage(next);
    },
    [totalPages]
  );

  /* ===========================
     effects
  =========================== */
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    loadPage();
  }, [loadPage]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">OWN VEHICLE MASTER REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Own Vehicle Master Report
              </h1>

              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {session.branchId || "-"} {" • "} User ID: {session.loginUserId || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span className={pillClass}>Total Records: {total}</span>
                <span className={pillClass}>Page: {page}</span>
                <span className={pillClass}>Page Size: {pageSize}</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowFilters((p) => !p)}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? `border-gray-600 text-white hover:bg-gray-800 ${
                        showFilters ? "bg-gray-800" : ""
                      }`
                    : `border-gray-300 text-gray-700 hover:bg-gray-50 ${
                        showFilters ? "bg-gray-50" : ""
                      }`
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={handleBack}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {pageLoading || dropdownLoading ? (
            <div className="text-center py-16">
              <div
                className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                  isDark ? "bg-gray-700" : "bg-gray-100"
                }`}
              >
                <FaInfoCircle className="w-10 h-10 text-gray-400" />
              </div>
              <h3
                className={`text-xl font-semibold mb-2 ${
                  isDark ? "text-gray-200" : "text-gray-800"
                }`}
              >
                Loading...
              </h3>
              <p
                className={`mb-6 max-w-sm mx-auto ${
                  isDark ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Please wait while data is loading.
              </p>
            </div>
          ) : (
            <>
              {/* Filters */}
              {showFilters && (
                <div
                  className={`rounded-xl p-6 mb-6 border ${
                    isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <h3
                    className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <FaFilter /> FILTRATION AREA
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className={labelClass}>Vehicle Type</label>
                      <select
                        name="vehicleType"
                        value={filters.vehicleType}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        {(vehicleTypes || []).map((v, idx) => (
                          <option key={`${v.value}-${idx}`} value={v.value}>
                            {v.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Branch</label>
                      <select
                        name="branch"
                        value={filters.branch}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={!canEditBranch || isBusy}
                        title={!canEditBranch ? "Branch user: branch is locked" : ""}
                      >
                        {(branches || []).map((b, idx) => (
                          <option key={`${b.value}-${idx}`} value={b.value}>
                            {b.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      {pageLoading
                        ? "Loading page..."
                        : dropdownLoading
                        ? "Loading dropdowns..."
                        : listLoading
                        ? "Loading list..."
                        : "Ready"}
                    </div>

                    <div className="flex gap-3 flex-wrap">
                      <button
                        onClick={handleSearch}
                        type="button"
                        className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2.5 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                        disabled={isBusy}
                      >
                        <FaSearch />
                        Search
                      </button>

                      <button
                        onClick={handleViewAll}
                        type="button"
                        className={`flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                        disabled={isBusy}
                      >
                        <FaEye />
                        View All
                      </button>

                      <button
                        onClick={handlePrintAll}
                        type="button"
                        className={`flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-6 py-2.5 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                        disabled={isBusy}
                      >
                        <FaPrint />
                        Print
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Results */}
              <div
                className={`rounded-xl shadow-sm border overflow-hidden ${
                  isDark ? "border-gray-700" : "border-gray-200"
                }`}
              >
                {rows.length === 0 ? (
                  <div className="text-center py-16">
                    <div
                      className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                        isDark ? "bg-gray-700" : "bg-gray-100"
                      }`}
                    >
                      <FaEye className="w-10 h-10 text-gray-400" />
                    </div>
                    <h3
                      className={`text-xl font-semibold mb-2 ${
                        isDark ? "text-gray-200" : "text-gray-800"
                      }`}
                    >
                      No Vehicle Types Found
                    </h3>
                    <p
                      className={`mb-6 max-w-sm mx-auto ${
                        isDark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Try adjusting your search criteria.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className={`${buttonClass} text-white`}>
                        <tr>
                          <th className="px-4 py-4 text-center font-semibold">S.No</th>
                          <th className="px-4 py-4 text-center font-semibold">Vehicle Type</th>
                          <th className="px-4 py-4 text-center font-semibold">Qty</th>
                          <th className="px-4 py-4 text-center font-semibold">
                            Total Purchase Value
                          </th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {currentItems.map((row, idx) => (
                          <tr
                            key={`${row.id}-${idx}`}
                            className={`${
                              isDark ? "bg-gray-800 hover:bg-gray-700" : "bg-white hover:bg-gray-50"
                            } transition-colors duration-200`}
                          >
                            <td
                              className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {(page - 1) * pageSize + idx + 1}
                            </td>

                            <td
                              onClick={() => handleOpenDetail(row)}
                              title="Open details"
                              className={`px-4 py-4 whitespace-nowrap text-sm text-center cursor-pointer underline ${
                                isDark ? "text-gray-200" : "text-gray-900"
                              }`}
                            >
                              {row.type}
                            </td>

                            <td
                              className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {row.qty}
                            </td>

                            <td
                              className={`px-4 py-4 whitespace-nowrap text-sm text-right ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {formatCurrency(row.value)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {total > 0 && (
                <div className="mt-6 flex justify-center items-center gap-3">
                  <button
                    onClick={() => goPage(page - 1)}
                    disabled={page <= 1 || isBusy}
                    type="button"
                    className={`px-3 py-1 border rounded text-sm ${
                      isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    } ${page <= 1 || isBusy ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    Prev
                  </button>

                  <span className={`${isDark ? "text-gray-300" : "text-gray-700"} text-sm`}>
                    Page <b>{page}</b> / {totalPages}
                  </span>

                  <button
                    onClick={() => goPage(page + 1)}
                    disabled={page >= totalPages || isBusy}
                    type="button"
                    className={`px-3 py-1 border rounded text-sm ${
                      isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    } ${page >= totalPages || isBusy ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    Next
                  </button>
                </div>
              )}

              {isBusy && (
                <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  {pageLoading
                    ? "Loading page..."
                    : dropdownLoading
                    ? "Loading dropdowns..."
                    : listLoading
                    ? "Loading list..."
                    : "Loading..."}
                </div>
              )}
            </>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}